import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import HeroCanvas from "@/components/HeroCanvas";
import EditorialMarquee from "@/components/Marquee";
import Magnetic from "@/components/Magnetic";
import ClientLogos from "@/components/ClientLogos";
import CaseStudies from "@/components/CaseStudies";
import Testimonials from "@/components/Testimonials";
import { RevealText, Reveal, EASE } from "@/components/RevealText";
import { getServices, getBlog, getCaseStudies, getClients, getTestimonials } from "@/lib/api";

/* ------------------------------- HERO ------------------------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const canvasY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const canvasScale = useTransform(scrollYProgress, [0, 1], [1, 1.25]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-[100svh] flex flex-col justify-end overflow-hidden" data-testid="hero">
      <motion.div
        style={{ y: canvasY, scale: canvasScale }}
        className="absolute inset-0 z-0 opacity-70"
      >
        <HeroCanvas />
      </motion.div>
      <div className="absolute inset-0 z-0 bg-gradient-to-t from-black via-black/40 to-black/70 pointer-events-none" />

      <motion.div style={{ y: textY, opacity: fade }} className="relative z-10 max-w-[1600px] w-full mx-auto px-6 md:px-10 pb-16 md:pb-24">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 1 }}
          className="font-mono text-xs md:text-sm tracking-[0.3em] uppercase text-white/50 mb-8 flex items-center gap-3"
        >
          <span className="w-8 h-px bg-[color:var(--accent)]" />
          Liinex Information Systems ®
        </motion.p>

        <h1 className="font-display uppercase tracking-tighter leading-[0.82] text-[15vw] md:text-[10.5vw]">
          <RevealText lines={["We build the"]} delay={0.35} />
          <span className="block">
            <RevealText lines={["software, web"]} delay={0.47} />
          </span>
          <span className="block text-white/25">
            <RevealText lines={["& AI you ship."]} delay={0.59} />
          </span>
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 1, ease: EASE }}
          className="mt-10 flex flex-col md:flex-row md:items-end justify-between gap-8"
        >
          <p className="font-mono text-sm md:text-base text-white/60 max-w-md leading-relaxed">
            A digital studio engineering web, software, Generative AI trainings and
            digital marketing for teams that refuse to be average.
          </p>
          <Magnetic>
            <Link
              to="/contact"
              data-testid="hero-cta"
              className="group inline-flex items-center gap-3 bg-white text-black px-8 py-5 font-mono text-xs tracking-[0.2em] uppercase hover:bg-[color:var(--accent)] hover:text-white transition-colors whitespace-nowrap"
            >
              Start a project
              <ArrowUpRight size={16} className="group-hover:rotate-45 transition-transform" />
            </Link>
          </Magnetic>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        style={{ opacity: fade }}
        className="absolute bottom-6 right-6 md:right-10 z-10 flex items-center gap-2 font-mono text-[10px] tracking-[0.25em] uppercase text-white/40"
      >
        Scroll <ArrowDown size={14} className="animate-bounce" />
      </motion.div>
    </section>
  );
}

/* ----------------------------- SERVICES ----------------------------- */
const SERVICE_IMAGES = {
  web: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  software: "https://images.unsplash.com/photo-1542831371-29b0f74f9713?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  genai: "https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  marketing: "https://images.unsplash.com/photo-1551649081-716ed4a46706?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};
const SPAN = {
  web: "md:col-span-7",
  software: "md:col-span-5",
  genai: "md:col-span-5",
  marketing: "md:col-span-7",
};

function ServiceCard({ s, showImage }) {
  return (
    <Reveal className={`${SPAN[s.id]} `}>
      <Link to={`/services/${s.id}`} className="group relative border border-white/10 bg-[color:var(--surface)] p-8 md:p-10 h-full min-h-[280px] md:min-h-[340px] flex flex-col justify-between overflow-hidden hover:border-white/30 transition-colors" data-testid={`service-${s.id}`}>
        {showImage && (
          <div className="absolute inset-0 opacity-20 group-hover:opacity-40 transition-opacity">
            <img src={SERVICE_IMAGES[s.id]} alt="" className="w-full h-full object-cover grayscale" />
          </div>
        )}
        <div className="relative flex items-start justify-between">
          <span className="font-mono text-xs tracking-[0.25em] text-[color:var(--accent)]">{s.index}</span>
          <ArrowUpRight size={22} className="text-white/40 group-hover:text-white group-hover:rotate-45 transition-all" />
        </div>
        <div className="relative">
          <h3 className="font-display text-3xl md:text-5xl uppercase tracking-tight leading-none mb-3">{s.title}</h3>
          <p className="font-mono text-sm text-white/50 max-w-md mb-6">{s.description}</p>
          <div className="flex flex-wrap gap-2">
            {s.capabilities.map((c) => (
              <span key={c} className="font-mono text-[10px] tracking-[0.15em] uppercase border border-white/15 px-3 py-1.5 text-white/60">
                {c}
              </span>
            ))}
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

function Services({ services }) {
  return (
    <section id="services" className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 scroll-mt-20" data-testid="services-section">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16 md:mb-24">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[color:var(--accent)]" /> What we do
          </p>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.85]">
            Four disciplines.<br /><span className="text-white/25">One standard.</span>
          </h2>
        </div>
        <p className="font-mono text-sm text-white/50 max-w-xs">
          [ 01 — 04 ] From first line of code to campaigns that compound, we operate as one integrated team.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
        {services.map((s) => (
          <ServiceCard key={s.id} s={s} showImage={s.id === "web" || s.id === "marketing"} />
        ))}
      </div>
    </section>
  );
}

/* ---------------------------- MANIFESTO ----------------------------- */
const CHAPTERS = [
  { n: "01", title: "Craft over templates", body: "We refuse the generic. Every interface, system and campaign is designed from first principles for your context — not pulled from a shelf." },
  { n: "02", title: "Engineering rigor", body: "Clean architecture, measurable performance and maintainable code. We build products that survive scale, not just launch day." },
  { n: "03", title: "AI, applied", body: "We don't chase hype. We teach and ship Generative AI that creates real leverage — from trainings to production systems." },
  { n: "04", title: "Growth that lasts", body: "Marketing built on compounding assets, not vanity spikes. Durable pipelines, honest metrics, real revenue." },
];

function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section id="studio" className="border-y border-white/10 bg-black scroll-mt-20" data-testid="manifesto-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <div className="lg:sticky lg:top-28">
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-6 flex items-center gap-3">
              <span className="w-8 h-px bg-[color:var(--accent)]" /> The Studio
            </p>
            <h2 className="font-display text-5xl md:text-6xl uppercase tracking-tighter leading-[0.85] mb-10">
              A manifesto,<br />not a<br />brochure.
            </h2>
            <div ref={ref} className="relative aspect-[4/5] overflow-hidden border border-white/10 hidden lg:block">
              <motion.img
                style={{ y: imgY, scale: 1.2 }}
                src="https://images.unsplash.com/photo-1771879808903-bfd5208ef6f9?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="Liinex studio architecture"
                className="w-full h-full object-cover grayscale"
              />
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 lg:col-start-6">
          {CHAPTERS.map((c) => (
            <Reveal key={c.n} className="relative border-t border-white/10 py-10 md:py-14 first:border-t-0">
              <span className="absolute -top-4 right-0 font-display text-7xl md:text-9xl text-white/5 select-none pointer-events-none leading-none">
                {c.n}
              </span>
              <div className="relative">
                <span className="font-mono text-xs tracking-[0.25em] text-[color:var(--accent)] mb-4 block">CH.{c.n}</span>
                <h3 className="font-display text-3xl md:text-5xl uppercase tracking-tight mb-4">{c.title}</h3>
                <p className="font-mono text-sm md:text-base text-white/55 max-w-lg leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------ STATS ------------------------------- */
const STATS = [
  { v: "120+", l: "Projects shipped" },
  { v: "40+", l: "Teams trained in AI" },
  { v: "8", l: "Years of craft" },
  { v: "∞", l: "Attention to detail" },
];
function Stats() {
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-20 md:py-28" data-testid="stats-section">
      <div className="grid grid-cols-2 md:grid-cols-4 border-l border-t border-white/10">
        {STATS.map((s, i) => (
          <Reveal key={s.l} delay={i * 0.08} className="border-r border-b border-white/10 p-8 md:p-12">
            <div className="font-display text-5xl md:text-7xl tracking-tighter mb-3">{s.v}</div>
            <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-white/45">{s.l}</div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ---------------------------- JOURNAL ------------------------------- */
function JournalPreview({ posts }) {
  if (!posts.length) return null;
  return (
    <section className="max-w-[1600px] mx-auto px-6 md:px-10 py-24 md:py-40" data-testid="journal-preview">
      <div className="flex items-end justify-between mb-14 md:mb-20">
        <div>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-white/40 mb-6 flex items-center gap-3">
            <span className="w-8 h-px bg-[color:var(--accent)]" /> The Journal
          </p>
          <h2 className="font-display text-5xl md:text-7xl uppercase tracking-tighter leading-[0.85]">Latest thinking</h2>
        </div>
        <Link to="/blog" className="hidden md:inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-white/60 hover:text-white transition-colors" data-testid="journal-view-all">
          View all <ArrowUpRight size={16} />
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {posts.slice(0, 3).map((p, i) => (
          <Reveal key={p.id} delay={i * 0.1}>
            <Link to={`/blog/${p.slug}`} className="group block border border-white/10 hover:border-white/30 transition-colors h-full" data-testid={`journal-card-${p.slug}`}>
              <div className="aspect-[16/10] overflow-hidden border-b border-white/10">
                <img src={p.cover} alt={p.title} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
              </div>
              <div className="p-7">
                <div className="flex items-center gap-3 font-mono text-[10px] tracking-[0.2em] uppercase text-white/40 mb-4">
                  <span className="text-[color:var(--accent)]">{p.category}</span><span>/</span><span>{p.read}</span>
                </div>
                <h3 className="font-display text-2xl uppercase tracking-tight leading-tight mb-3 group-hover:text-white transition-colors">{p.title}</h3>
                <p className="font-mono text-sm text-white/50 line-clamp-2">{p.excerpt}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

/* ------------------------------ PAGE -------------------------------- */
export default function Home() {
  const [services, setServices] = useState([]);
  const [posts, setPosts] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);
  const [clients, setClients] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    getServices().then(setServices).catch(() => {});
    getBlog().then(setPosts).catch(() => {});
    getCaseStudies().then(setCaseStudies).catch(() => {});
    getClients().then(setClients).catch(() => {});
    getTestimonials().then(setTestimonials).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Hero />
      <EditorialMarquee />
      {services.length > 0 && <Services services={services} />}
      <ClientLogos clients={clients} />
      <CaseStudies items={caseStudies} />
      <Testimonials items={testimonials} />
      <Manifesto />
      <Stats />
      <JournalPreview posts={posts} />
    </div>
  );
}
